import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from '@/lib/api/types';
import { withArabicPlurals } from './plurals';

/**
 * The chrome locale this browser last used, cached so a reload does not paint
 * the console in English before `/admin/me` answers.
 *
 * The profile is the source of truth — it is what follows an operator to
 * another machine — and {@link applyChromeLocale} is called again with
 * `user.locale` as soon as the session loads. This is only the fast path, and
 * it is also the whole persistence story for the header toggle, which is a
 * per-device choice made in one click.
 *
 * localStorage throws in some privacy modes; an unreadable preference is the
 * same as none.
 */
const LOCALE_KEY = 'svet.locale';

function readStoredLocale(): Locale | null {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(LOCALE_KEY);
    return raw === 'ar' || raw === 'en' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    localStorage?.setItem(LOCALE_KEY, locale);
  } catch {
    /* Quota or a locked-down profile — the cache is an optimisation, not state. */
  }
}

/**
 * The copy is fetched, not bundled into the entry chunk.
 *
 * `en.ts` and `ar.ts` are ~157 kB and ~202 kB of source. Imported statically
 * they landed in the eager chunk, so every visitor downloaded BOTH languages
 * before first paint — including an editor who will never open the screens most
 * of those strings belong to (F-21). Split, the active language is one small
 * chunk fetched in parallel with the app, and the other is fetched only if
 * someone switches to it.
 *
 * `en` is always loaded because it is the fallback: an Arabic key the bundle
 * has not translated must render English, not its own key.
 */
const bundles: Record<Locale, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en'),
  ar: () => import('./locales/ar'),
};

export async function loadLocale(locale: Locale): Promise<void> {
  if (i18n.hasResourceBundle(locale, 'translation')) return;
  const mod = await bundles[locale]();
  const copy = locale === 'ar' ? withArabicPlurals(mod.default) : mod.default;
  i18n.addResourceBundle(locale, 'translation', copy, true, true);
}

/**
 * Boot i18next with the language this browser last used, and wait for its copy
 * before the first render — a console that paints its keys and then swaps them
 * for words is worse than one that waits a frame for the words.
 *
 * Not a hard-coded 'en': the console shipped a complete Arabic bundle that no
 * page load could ever reach, because boot never asked what the operator had
 * chosen (F-26). An Arabic-speaking operator got an English LTR console on
 * every reload, deep link and 401 refresh.
 */
export async function initI18n(): Promise<void> {
  const initial = readStoredLocale() ?? 'en';
  await i18n.use(initReactI18next).init({
    resources: {},
    lng: initial,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  await Promise.all(initial === 'en' ? [loadLocale('en')] : [loadLocale('en'), loadLocale(initial)]);
  applyChromeLocale(initial, { persist: false });
}

/**
 * The writing direction a chrome locale is read in.
 *
 * Exported because `<html dir>` is not the only consumer: Radix primitives take
 * their own `dir` prop and default to `"ltr"` when it is absent, regardless of
 * what the document says — a Radix subtree renders a real `dir="ltr"` element,
 * which OVERRIDES the inherited direction rather than merely failing to follow
 * it. So the direction has to be handed to them explicitly, and it has to come
 * from here: two copies of `locale === 'ar'` are two places to forget a third
 * RTL locale.
 */
export function directionFor(locale: Locale | (string & {})): 'rtl' | 'ltr' {
  // Widened past `Locale` for one caller: i18next types `language` as a plain string,
  // and casting it back would assert something this module cannot check. Anything that
  // is not a language we ship reads left-to-right, which is also the fallback locale.
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Apply chrome locale: set i18next language + <html lang/dir> (spec §5.3 / §6).
 *
 * `persist: false` is for the caller that has just written the locale to the
 * profile itself, or is only replaying what was already stored.
 */
export function applyChromeLocale(locale: Locale, opts?: { persist?: boolean }): void {
  // The document itself has to be told, not only i18next: `lang` and `dir` are
  // what give an Arabic console its RTL layout, and index.html ships lang="en".
  // Set first and synchronously — the direction of the page must not wait on a
  // network fetch of the copy.
  const html = document.documentElement;
  html.setAttribute('lang', locale);
  html.setAttribute('dir', directionFor(locale));
  if (opts?.persist !== false) writeStoredLocale(locale);
  void loadLocale(locale)
    .then(() => i18n.changeLanguage(locale))
    // A chunk that will not load leaves the console in the language it is
    // already showing, which is the only safe failure here.
    .catch(() => undefined);
}

export default i18n;
